/**
 * Vehicle data formatting utilities
 * Pure functions for vehicle data processing - can be used in both client and server contexts
 */

import { EfficiencyLabelShort } from "@/app/components/offers/efficiency-label";
import { cn } from "@/lib/utils";
import type { TEfficiencyLabel } from "@/app/components/offers/efficiency-label";
import type { ReactNode } from "react";

export type EnvkvT = {
  required?: boolean;
  envkvtype?: string;
  emissionscombined?: number | null;
  emissionscombinedweighted?: number | null;
  consumptionlow?: number | null;
  consumptionmedium?: number | null;
  consumptionhigh?: number | null;
  consumptionextrahigh?: number | null;
  consumptioncombined?: number | null;
  consumptioncombinedweighted?: number | null;
  consumptionunit?: string | null;
  electricconsumptionlow?: number | null;
  electricconsumptionmedium?: number | null;
  electricconsumptionhigh?: number | null;
  electricconsumptionextrahigh?: number | null;
  electricconsumptioncombined?: number | null;
  electricconsumptioncombinedweighted?: number | null;
  energycosts?: number | null;
  tax?: number | null;
  co2costslow?: number | null;
  co2costsmedium?: number | null;
  co2costshigh?: number | null;
  emissionsclass?: string | null;
  emissionsclassweighted?: string | null;
  [key: string]: unknown;
};

export type SellingDealerT = {
  phone?: string;
  firm?: string;
  street?: string;
  dealercountry?: string;
  postalcode?: string;
  city?: string;
  firmextra?: string;
  [key: string]: unknown;
};

const EFFICIENCY_LABELS: ReadonlyArray<TEfficiencyLabel> = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
];

function toEfficiencyLabel(
  value?: string | null
): TEfficiencyLabel | undefined {
  if (!value) return undefined;
  const upperValue = value.toUpperCase();
  return EFFICIENCY_LABELS.includes(upperValue as TEfficiencyLabel)
    ? (upperValue as TEfficiencyLabel)
    : undefined;
}

/**
 * Formats price values to German currency format
 */
export function formatPrice(
  price: number | string | null,
  fractionDigits: number = 0
): string | null {
  if (price === null || price === undefined) return null;

  const safeDigits = Number.isFinite(fractionDigits)
    ? Math.min(Math.max(Math.floor(fractionDigits), 0), 20)
    : 0;

  return Number(price).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: safeDigits,
    maximumFractionDigits: safeDigits,
  });
}

/**
 * Formats date strings to German date format
 */
export function formatDate(dateString: string | null): string | null {
  if (!dateString || dateString === "1970-01-01" || dateString === null) {
    return null;
  }

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE");
  } catch (error) {
    console.error("Error formatting date:", error);
    return null;
  }
}

/**
 * Formats power from kW to PS (German horsepower)
 */
export function formatPower(powerInKw: number | string | null): string | null {
  if (powerInKw === null || powerInKw === undefined) return null;

  const kw = Number(powerInKw);
  if (isNaN(kw)) return null;

  const powerInPs = Math.round(kw * 1.35962);
  return `${kw} kW (${powerInPs} PS)`;
}

/**
 * Formats kilometers with unit
 */
export function formatKilometers(kilometers: number | string | null): string {
  const km = `${kilometers || 0} km`;
  return km === "null km" ? "0 km" : km;
}

/**
 * Formats warranty flag to German text
 */
export function formatWarranty(warrantyFlag: number | boolean | null): string {
  return warrantyFlag ? "Ja" : "Nein";
}

/**
 * Formats VAT flag to German text
 */
export function formatVatFlag(vatFlag: number | null): string {
  return vatFlag === 1
    ? "inkl. MwSt. (ausweisbar)"
    : "inkl. MwSt. (nicht ausweisbar)";
}

/**
 * Formats dealer address from selling dealer data
 */
export function formatAddress(sellingDealer: SellingDealerT) {
  return `${sellingDealer?.firm || ""}, ${sellingDealer?.street || ""},${sellingDealer?.dealercountry || ""}-${sellingDealer?.postalcode || ""} ${sellingDealer?.city || ""}`;
}

/**
 * Formats battery range with unit
 */
export function formatBatteryRange(
  range: number | string | null
): string | undefined {
  return range ? `${range} km` : undefined;
}

/**
 * Formats numbers with specified decimal places
 */
export function formatNumber(
  value: number | string | null | undefined,
  decimals: number = 2
): string | null {
  if (value === null || value === undefined) return null;
  const number = Number(value);
  return !isNaN(number) ? number.toFixed(decimals).replace(".", ",") : null;
}

/**
 * Formats cost values to German currency format
 */
export function formatCost(cost: number | string | null): string | null {
  if (cost !== null && cost !== undefined) {
    return Number(cost).toLocaleString("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
  return null;
}

/**
 * Formats annual tax cost
 */
export function formatTaxCost(tax: number | string | null): string | null {
  const formattedCost = formatCost(tax);
  return formattedCost ? `${formattedCost}/Jahr` : null;
}

/**
 * Determines department ID based on street address
 */
export function getDepartmentId(street: string): string {
  if (street.includes("Hilpertstraße")) return "HMRDyFPJ8r";
  if (street.includes("Riedstraße")) return "0YECuDwGM6";
  if (street.includes("Robert-Bosch-Straße")) return "Z1kl9nJxiH";
  if (street.includes("Bürstädter Straße")) return "asSyo7QtVQ";
  if (street.includes("Max-Eyth-Weg")) return "NoKi5N2cTr";
  return "";
}

/**
 * Generates SEO-friendly slug for vehicle
 * Format: shortname-vehicleid (e.g., "audi-s6-avant-12345")
 */
export function generateSlug(
  shortName: string,
  vehicleId: number | string
): string {
  const cleanShortName = String(shortName)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing dashes

  return `${cleanShortName}-${vehicleId}`;
}

/**
 * Determines economy status based on notes
 */
export function getEconomyStatus(notes: string | null): string {
  return notes && String(notes).includes("übermäßige Gebrauchsspuren")
    ? "Economy Drive"
    : "false";
}

/**
 * Formats consumption data for hybrid vehicles
 */
export function formatHybridConsumption(
  fuelConsumption: number | null,
  electricConsumption: number | null
): string | null {
  const defaultUnit = "l/100km";
  const defaultElectricUnit = "kWh/100km";

  if (fuelConsumption && electricConsumption) {
    return `${formatNumber(fuelConsumption)} ${defaultUnit} + ${formatNumber(electricConsumption)} ${defaultElectricUnit}`;
  }
  return null;
}

/**
 * Formats electric consumption with unit
 */
export function formatElectricConsumption(
  consumption: number | string | null
): string | undefined {
  const defaultElectricUnit = "kWh/100km";
  return consumption
    ? `${formatNumber(consumption)} ${defaultElectricUnit}`
    : undefined;
}

/**
 * Processes consumption and emission data for combustion vehicles
 */
export function processCombustionData(envkv: EnvkvT): void {
  if (envkv?.required && envkv.envkvtype === "combustion") {
    // Format emission data
    const emissions = envkv.emissionscombined;
    envkv.emissionscombined = emissions
      ? Number(`${formatNumber(emissions)}`.replace(",", "."))
      : 0;

    // Format consumption data for various driving scenarios
    const consumptionFields = [
      "consumptionlow",
      "consumptionmedium",
      "consumptionhigh",
      "consumptionextrahigh",
      "consumptioncombined",
    ] as const;

    consumptionFields.forEach((field) => {
      if (envkv[field]) {
        envkv[field] = Number(
          `${formatNumber(envkv[field])}`.replace(",", ".")
        );
      }
    });
  }
}

/**
 * Processes consumption and emission data for hybrid vehicles
 */
export function processHybridData(envkv: EnvkvT): void {
  if (envkv?.required && envkv.envkvtype === "plugin-hybrid") {
    // Format emission data
    const emissionsWeighted = envkv.emissionscombinedweighted;
    envkv.emissionscombinedweighted = emissionsWeighted
      ? Number(`${formatNumber(emissionsWeighted)}`.replace(",", "."))
      : 0;

    const emissions = envkv.emissionscombined;
    envkv.emissionscombined = emissions
      ? Number(`${formatNumber(emissions)}`.replace(",", "."))
      : 0;

    // Format fuel consumption data
    const fuelConsumptionFields = [
      "consumptionlow",
      "consumptionmedium",
      "consumptionhigh",
      "consumptionextrahigh",
      "consumptioncombined",
    ] as const;

    fuelConsumptionFields.forEach((field) => {
      if (envkv[field]) {
        envkv[field] = Number(
          `${formatNumber(envkv[field])}`.replace(",", ".")
        );
      }
    });

    // Format electric consumption data
    const electricConsumptionFields = [
      "electricconsumptionlow",
      "electricconsumptionmedium",
      "electricconsumptionhigh",
      "electricconsumptionextrahigh",
      "electricconsumptioncombined",
    ] as const;

    electricConsumptionFields.forEach((field) => {
      if (envkv[field]) {
        envkv[field] = Number(
          `${formatNumber(envkv[field])}`.replace(",", ".")
        );
      }
    });
    console.log(
      "envkv.consumptioncombinedweighted",
      envkv.consumptioncombinedweighted
    );
    // Format combined hybrid consumption
    const consumptioncombinedhybrid = formatHybridConsumption(
      envkv.consumptioncombinedweighted!,
      envkv.electricconsumptioncombinedweighted!
    );
    (envkv as EnvkvT).consumptioncombinedhybrid = consumptioncombinedhybrid;

    envkv.consumptioncombinedweighted = envkv.consumptioncombined
      ? Number(String(envkv.consumptioncombined).replace(",", "."))
      : undefined;
  }
}

/**
 * Processes consumption and emission data for electric vehicles
 */
export function processElectricData(envkv: EnvkvT): void {
  if (envkv?.required && envkv.envkvtype === "electric") {
    // Format emission data
    const emissions = envkv.emissionscombined;
    envkv.emissionscombined = emissions
      ? Number(`${formatNumber(emissions)}`.replace(",", "."))
      : 0;

    // Format electric consumption data
    const electricConsumptionFields = [
      "electricconsumptionlow",
      "electricconsumptionmedium",
      "electricconsumptionhigh",
      "electricconsumptionextrahigh",
      "electricconsumptioncombined",
    ] as const;

    electricConsumptionFields.forEach((field) => {
      if (envkv[field]) {
        envkv[field] = Number(
          `${formatNumber(envkv[field])}`.replace(",", ".")
        );
      }
    });
  }
}

/**
 * Formats environmental data (envkv) based on vehicle type
 */
export function formatEnvironmentalData(envkv: EnvkvT): void {
  if (!envkv) return;

  // Process vehicle type specific data
  processCombustionData(envkv);
  processHybridData(envkv);
  processElectricData(envkv);

  // Format cost fields
  // @ts-expect-error - unknown autohausen structure //TODO: fix
  envkv.energycosts = formatCost(envkv.energycosts);
  // @ts-expect-error - unknown autohausen structure //TODO: fix
  envkv.tax = formatTaxCost(envkv.tax);
  // @ts-expect-error - unknown autohausen structure //TODO: fix
  envkv.co2costslow = formatCost(envkv.co2costslow);
  // @ts-expect-error - unknown autohausen structure //TODO: fix
  envkv.co2costsmedium = formatCost(envkv.co2costsmedium);
  // @ts-expect-error - unknown autohausen structure //TODO: fix
  envkv.co2costshigh = formatCost(envkv.co2costshigh);

  // Format electric consumption combined weighted
  if (envkv.electricconsumptioncombinedweighted) {
    // @ts-expect-error - unknown autohausen structure //TODO: fix
    envkv.electricconsumptioncombinedweighted = formatElectricConsumption(
      envkv.electricconsumptioncombinedweighted
    );
  }
}

type ConsumptionLineT = {
  key: string;
  text: string;
  label?: TEfficiencyLabel;
};

function renderConsumptionLine(
  line: ConsumptionLineT,
  textSize: string = "text-sm"
): ReactNode {
  if (!line.text) return null;

  return (
    <div
      key={line.key}
      className={cn("flex flex-wrap items-center gap-2 font-normal", textSize)}
    >
      <span>
        {line.text}
        {line.label ? (
          <EfficiencyLabelShort
            className="mb-0.5 ml-2 inline-block"
            label={line.label}
          />
        ) : null}
      </span>
    </div>
  );
}

function buildHybridLines(envkv: EnvkvT): ConsumptionLineT[] {
  const lines: ConsumptionLineT[] = [];

  const weightedConsumptionParts: string[] = [];
  const weightedFuel = formatNumber(envkv.consumptioncombinedweighted);
  const weightedElectric = formatNumber(
    envkv.electricconsumptioncombinedweighted
  );
  const weightedEmissions = formatNumber(envkv.emissionscombinedweighted);

  if (weightedFuel || weightedElectric) {
    const hybridSegments: string[] = [];
    if (weightedFuel) hybridSegments.push(`${weightedFuel} l/100km`);
    if (weightedElectric) {
      hybridSegments.push(`${weightedElectric} kWh/100km`);
    }

    weightedConsumptionParts.push(
      `Kraftstoffverbrauch (gewichtet, kombiniert): ${hybridSegments.join(
        " + "
      )}`
    );
  }

  if (weightedEmissions) {
    weightedConsumptionParts.push(
      `CO₂-Emissionen (gewichtet, kombiniert): ${weightedEmissions} g/km`
    );
  }

  if (weightedConsumptionParts.length) {
    lines.push({
      key: "weighted",
      text: weightedConsumptionParts.join(", "),
      label: toEfficiencyLabel(envkv.emissionsclassweighted),
    });
  }

  const dischargedParts: string[] = [];
  const dischargedFuel = formatNumber(envkv.consumptioncombined);
  const dischargedEmissions = formatNumber(envkv.emissionscombined);

  if (dischargedFuel) {
    dischargedParts.push(
      `Kraftstoffverbrauch bei entladener Batterie (kombiniert): ${dischargedFuel} l/100km`
    );
  }

  if (dischargedEmissions) {
    dischargedParts.push(
      `CO₂-Emissionen bei entladener Batterie: ${dischargedEmissions} g/km`
    );
  }

  if (dischargedParts.length) {
    lines.push({
      key: "battery",
      text: dischargedParts.join(", "),
      label: toEfficiencyLabel(envkv.emissionsclass),
    });
  }

  return lines;
}

function buildElectricLine(envkv: EnvkvT): ConsumptionLineT | null {
  const parts: string[] = [];
  const electricConsumption = formatNumber(envkv.electricconsumptioncombined);
  if (electricConsumption) {
    parts.push(`Stromverbrauch (kombiniert): ${electricConsumption} kWh/100km`);
  }

  const emissions = formatNumber(envkv.emissionscombined);
  if (emissions) {
    parts.push(`CO₂-Emissionen: ${emissions} g/km`);
  }

  if (!parts.length) return null;

  return {
    key: "electric",
    text: parts.join(", "),
    label: toEfficiencyLabel(envkv.emissionsclass),
  };
}

function buildCombustionLine(envkv: EnvkvT): ConsumptionLineT | null {
  const parts: string[] = [];
  const fuelConsumption = formatNumber(envkv.consumptioncombined);
  const unitCandidate = envkv.consumptionunit ?? undefined;
  const consumptionUnit =
    typeof unitCandidate === "string" && unitCandidate.trim().length > 0
      ? unitCandidate
      : "l/100km";

  if (fuelConsumption) {
    parts.push(
      `Kraftstoffverbrauch (kombiniert): ${fuelConsumption} ${consumptionUnit}`
    );
  }

  const emissions = formatNumber(envkv.emissionscombined);
  if (emissions) {
    parts.push(`CO₂-Emissionen: ${emissions} g/km`);
  }

  if (!parts.length) return null;

  return {
    key: "combustion",
    text: parts.join(", "),
    label: toEfficiencyLabel(envkv.emissionsclass),
  };
}

/**
 * Renders pre-formatted JSX for envkv consumption data depending on drivetrain type.
 * @param envkvInput - The envkv data object
 * @param textSize - Optional text size class (default: "text-sm")
 */
export function renderEnvkvConsumption(
  envkvInput?: unknown,
  textSize: string = "text-sm"
): ReactNode {
  if (!envkvInput || typeof envkvInput === "string") {
    return null;
  }

  if (typeof envkvInput !== "object") {
    return null;
  }

  const envkv = envkvInput as EnvkvT;

  if (!envkv.required) return null;

  if (envkv.envkvtype === "plugin-hybrid") {
    const lines = buildHybridLines(envkv);
    if (!lines.length) return null;

    return (
      <div className={cn("space-y-2 leading-relaxed", textSize)}>
        {lines.map((line) => renderConsumptionLine(line, textSize))}
      </div>
    );
  }

  if (envkv.envkvtype === "electric") {
    const electricLine = buildElectricLine(envkv);
    if (!electricLine) return null;

    return (
      <div className={cn("leading-relaxed", textSize)}>
        {renderConsumptionLine(electricLine, textSize)}
      </div>
    );
  }

  const combustionLine = buildCombustionLine(envkv);
  if (!combustionLine) return null;

  return (
    <div className={cn("leading-relaxed", textSize)}>
      {renderConsumptionLine(combustionLine, textSize)}
    </div>
  );
}
